import { NgTemplateOutlet } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  output,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors } from '@core/account-data';
import type { CurrentUser, User, UserBase } from '@core/account-types';
import type { FullCandidate } from '@core/candidate-types';
import { FeatureSpecFacade } from '@core/feature-spec-logic';
import { TranslatePipe } from '@core/i18n-legacy';
import type { OfferSelectOption } from '@core/offers-ui';
import type { ConnectedOverlayDirective, SelectGroupedOptions } from '@core/user-interface-legacy';
import {
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownWidth,
  IconButtonComponent,
  NestedTemplateTriggerDirective,
  NewItemButtonComponent,
  RtStatefulComponent,
  RtTooltip,
} from '@core/user-interface-legacy';

import { injectAddEvaluationDropdownTranslations } from '../translations';
import { EvaluationFormsTabComponent } from './evaluation-forms-tab/evaluation-forms-tab.component';
import { RequestEvaluationTabComponent } from './request-evaluation-tab/request-evaluation-tab.component';

type State = {
  selectedTab: string | null;
  hasOffers: boolean;
  currentAdmin: CurrentUser | null;
};

@Component({
  selector: 'rt-add-evaluation-dropdown',
  templateUrl: 'add-evaluation-dropdown.component.html',
  styleUrls: ['./add-evaluation-dropdown.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownComponent,
    NgTemplateOutlet,
    NewItemButtonComponent,
    NestedTemplateTriggerDirective,
    RequestEvaluationTabComponent,
    EvaluationFormsTabComponent,
    DropdownHeaderComponent,
    IconButtonComponent,
    RtTooltip,
    RouterLink,
    TranslatePipe,
  ],
})
export class AddEvaluationDropdownComponent extends RtStatefulComponent<State> {
  private readonly store = inject(Store);
  private readonly featureSpecDataHelpers = inject(FeatureSpecFacade);

  protected readonly cdRef: ChangeDetectorRef;

  public readonly translations = injectAddEvaluationDropdownTranslations();

  public readonly offers = input<OfferSelectOption[]>([]);
  public readonly templates = input<SelectGroupedOptions[]>([]);
  public readonly dropdownRef = input<ConnectedOverlayDirective>(undefined!);
  public readonly schedule = input<boolean>(false);
  public readonly admins = input<UserBase[]>([]);
  public readonly allowedUsers = input<User[]>();
  public readonly disallowedUsers = input<User[]>();
  public readonly fromHiddenEvaluations = input<boolean>(false);
  public readonly forceNestedDropdown = input<'evaluationForms' | 'evaluationRequest'>();
  public readonly candidate = input<FullCandidate>();
  public readonly candidateId = input<number>();
  public readonly offerId = input<number | null>();

  public submit = output<{
    type: string;
    value: {
      talentPoolId?: number;
      offerId?: number;
      templateId: number;
      admins: User[];
    };
  }>();
  public close = output<void>();

  @ViewChild(DropdownComponent, { static: true }) public dropdownComponent: DropdownComponent;
  @ViewChild('evaluationRequestTpl', { static: true })
  public evaluationRequestTpl: TemplateRef<any>;
  @ViewChild('evaluationFormsTpl', { static: true }) public evaluationFormsTpl: TemplateRef<any>;

  public canManageInterviewTemplates: boolean;
  public DropdownWidth: typeof DropdownWidth = DropdownWidth;

  private ngOnDestroy$: Subject<void> = new Subject<void>();

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      selectedTab: null,
      hasOffers: false,
      currentAdmin: null,
    });
  }

  public ngOnChanges(): void {
    const offers = this.offers();
    this.setState({
      ...this.state,
      hasOffers: offers && !!offers.length,
    });
  }

  public ngOnInit(): void {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(currentAdmin => {
        this.setState({ ...this.state, currentAdmin });

        if (this.forceNestedDropdown()) {
          this.setNestedDropdownTpl();
        }
      });

    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabledMap => {
        this.canManageInterviewTemplates = enabledMap[Ability.ManageInterviewTemplates];
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public submitNewInterview(value: {
    talentPoolId?: number;
    offerId?: number;
    templateId: number;
    admins: User[];
  }): void {
    this.submit.emit({ value: value, type: 'addInterview' });
    this.closeDropdown();
  }

  public submitInterviewRequest(value: {
    talentPoolId?: number;
    offerId?: number;
    templateId: number;
    admins: User[];
  }): void {
    this.submit.emit({ value: value, type: 'addInterviewRequest' });
  }

  public closeDropdown(): void {
    this.close.emit();
    this.dropdownRef().open = false;
  }

  private setNestedDropdownTpl(): void {
    const tpl =
      this.forceNestedDropdown() === 'evaluationForms'
        ? this.evaluationFormsTpl
        : this.evaluationRequestTpl;
    this.dropdownComponent.setNestedTemplate(tpl);
  }
}
